/**
 * Read-side offerings hooks: paginated list, infinite list (placeholder), single.
 *
 * Cache keys / query semantics MUST stay identical to the prior inline
 * implementation — these hooks are composed back into `useOfferingHooks`.
 */

import { useQuery } from "@tanstack/react-query";
import type { Offering } from "@/lib/types/database/offerings";
import { offeringsKeys } from "./offerings-query-keys";
import { fetchOfferingById, fetchOfferingsPaginated } from "./offerings-fetchers";

interface PaginatedReturn {
  data:
    | {
        data: Offering[];
        totalCount: number;
        pageInfo: { hasNextPage: boolean; endCursor: string };
      }
    | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function usePaginatedOfferings(params?: Record<string, unknown>): PaginatedReturn {
  const first = (params?.first as number) ?? 100;
  const after = params?.after as string | null | undefined;
  const filter = params?.filter as Record<string, unknown> | undefined;

  const offset = after != null && after !== "" ? parseInt(String(after), 10) || 0 : 0;

  const query = useQuery({
    queryKey: offeringsKeys.paginated(first, offset, filter),
    queryFn: () => fetchOfferingsPaginated(first, offset, filter),
  });

  return {
    data: query.data
      ? { data: query.data.data, totalCount: query.data.totalCount, pageInfo: query.data.pageInfo }
      : undefined,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => query.refetch(),
  };
}

interface InfiniteReturn {
  data: undefined;
  isLoading: false;
  error: null;
  refetch: () => void;
  hasNextPage: false;
  isFetchingNextPage: false;
  fetchNextPage: () => void;
}

/**
 * Placeholder — Offerings list does not currently use infinite pagination.
 * Returns a static empty result shape to satisfy `EntityHooks<Offering>`.
 */
export function useInfiniteOfferings(): InfiniteReturn {
  return {
    data: undefined,
    isLoading: false,
    error: null,
    refetch: () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {},
  };
}

interface SingleReturn {
  data: Offering | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useSingleOffering(id: string, options?: { enabled?: boolean }): SingleReturn {
  const query = useQuery({
    queryKey: offeringsKeys.single(id),
    queryFn: () => fetchOfferingById(id),
    enabled: options?.enabled !== false && !!id,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => query.refetch(),
  };
}
